/**
 * アカウント案内の受け渡し(2026-09-03 発注者指示)。
 *
 * メール送信はやめ、**LINEで送る**か、文章をコピーして渡す。
 * 「LINEで送る」はLINEの共有画面を開き、送る相手を選んでそのまま送れる。
 *
 * ※LINEの共有URLは長すぎると切られるため、URL・社内ID・パスワードを先頭に置く。
 */
import { useState } from 'react';
import Modal from '@core/components/Modal';
import { useToast } from '@core/components/Toast';

/** 貼り付け・共有用の文章を組み立てる(大事な情報を先に置く) */
export function buildAccountNotice(params: {
  displayName: string;
  loginId: string;
  password: string;
  url: string;
}): string {
  const { displayName, loginId, password, url } = params;
  return [
    `${displayName}さん`,
    '経費申請システム「Nexus Gate」のログイン情報です。',
    '',
    `URL: ${url}`,
    `社内ID: ${loginId}`,
    `パスワード: ${password || '(担当者からお伝えします)'}`,
    '',
    '※メールアドレスではログインできません。社内IDを入力してください。',
    '※ログイン後、メニューの「パスワード変更」から変更できます。',
    '※使い方は、メニューの「使い方ガイド」をご覧ください。',
  ].join('\n');
}

export default function AccountNoticeModal({ text, onClose }: { text: string; onClose: () => void }) {
  const toast = useToast();
  const [copied, setCopied] = useState(false);
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  /** LINEを開いて、送る相手を選んでもらう */
  function sendByLine() {
    const url = `https://line.me/R/share?text=${encodeURIComponent(text)}`;
    const win = window.open(url, '_blank', 'noopener');
    // ポップアップが止められた場合は、この画面のままLINEへ移動する
    if (!win) window.location.href = url;
  }

  /** スマホの共有メニュー(LINE・メールなどから選べる) */
  async function shareOther() {
    try {
      await navigator.share({ text });
    } catch {
      // 共有をやめた場合は何もしない
    }
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // 古い端末やhttp接続ではclipboardが使えないので、選択して手動コピーしてもらう
      const area = document.getElementById('notice-text') as HTMLTextAreaElement | null;
      area?.select();
      if (!document.execCommand?.('copy')) {
        toast('コピーできませんでした。文章を選んでコピーしてください');
        return;
      }
    }
    setCopied(true);
    toast('コピーしました。LINEなどに貼り付けてください');
  }

  return (
    <Modal onClose={onClose} width="min(560px, 94vw)" showClose>
      <h3 style={{ marginBottom: 4 }}>ログイン情報を本人に送る</h3>
      <p className="hint" style={{ marginBottom: 12 }}>
        「LINEで送る」を押すとLINEが開きます。送る相手を選んでください。
        <br />
        LINEを使わない場合は「コピーする」で文章をコピーできます。内容は送る前に書き換えられます。
      </p>
      <textarea
        id="notice-text"
        readOnly
        value={text}
        style={{ width: '100%', minHeight: 260, fontFamily: 'inherit', fontSize: 13, lineHeight: 1.7 }}
      />
      <div className="modal-actions">
        <button className="btn-line-send" onClick={sendByLine}>
          LINEで送る
        </button>
        <button className="btn-line" onClick={() => void copy()}>
          {copied ? 'コピーしました' : 'コピーする'}
        </button>
        {canShare && (
          <button className="btn-line" onClick={() => void shareOther()}>
            他のアプリで送る
          </button>
        )}
        <button className="btn-line" onClick={onClose}>
          閉じる
        </button>
      </div>
    </Modal>
  );
}
